import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, Lock, Plus, CheckCircle2, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGARME_TOKENS_URL = "https://api.pagar.me/core/v5/tokens";
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatNumber = (v) => v.replace(/\D/g, "").slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ");
const formatExpiry = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };

async function tokenizeCard(publicKey, card) {
  const [mm, yy] = card.expiry.split("/");
  const res = await fetch(`${PAGARME_TOKENS_URL}?appId=${encodeURIComponent(publicKey)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: "card", card: {
      number: card.number.replace(/\s/g, ""), holder_name: card.holder.trim().toUpperCase(),
      exp_month: Number(mm), exp_year: 2000 + Number(yy), cvv: card.cvv,
    } }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.id) throw new Error(data.message || "Não foi possível validar o cartão. Confira os dados.");
  return data.id;
}

export function SavedCardRow({ card, selected, onSelect, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onSelect}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
      <div className="w-10 h-7 rounded bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-[10px] font-bold uppercase">{(card.brand || "card").slice(0, 4)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">•••• •••• •••• {card.last_four}</div>
        <div className="text-[11px] text-muted-foreground truncate">{card.holder_name} · {String(card.exp_month).padStart(2, "0")}/{String(card.exp_year).slice(-2)}</div>
      </div>
      {card.is_default && <span className="text-[10px] font-semibold text-primary flex items-center gap-1"><Zap className="w-3 h-3" /> Rápido</span>}
      {selected && <CheckCircle2 className="w-4 h-4 text-primary" />}
    </button>
  );
}

export default function CardPaymentDialog({ open, onOpenChange, amount, purpose, referenceId, packageId, title = "Pagar com cartão de crédito", description, onSuccess }) {
  const [status, setStatus] = useState(null);
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState("new");
  const [form, setForm] = useState({ number: "", holder: "", expiry: "", cvv: "" });
  const [saveCard, setSaveCard] = useState(true);
  const [installments, setInstallments] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null); setBusy(false); setInstallments(1); setForm({ number: "", holder: "", expiry: "", cvv: "" });
    axios.get(`${API}/pagarme/status`).then(r => setStatus(r.data)).catch(() => setStatus({ configured: false }));
    axios.get(`${API}/pagarme/cards`).then(r => {
      const list = r.data || [];
      setCards(list);
      const def = list.find(c => c.is_default) || list[0];
      setSelected(def ? def.card_id : "new");
    }).catch(() => setCards([]));
  }, [open]);

  const options = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1).filter(n => amount / n >= 5 || n === 1), [amount]);
  const perInstallment = amount / installments;
  const isNew = selected === "new";
  const formValid = !isNew || (form.number.replace(/\s/g, "").length >= 13 && form.holder.trim().length >= 3 && /^\d{2}\/\d{2}$/.test(form.expiry) && form.cvv.length >= 3);

  const pay = async () => {
    if (!status?.public_key) { toast.error("Gateway Pagar.me não configurado"); return; }
    setBusy(true);
    try {
      const body = { purpose, amount, installments, reference_id: referenceId || null, package_id: packageId || null };
      if (isNew) {
        body.card_token = await tokenizeCard(status.public_key, form);
        if (saveCard) body.save_token = await tokenizeCard(status.public_key, form);
      } else {
        body.card_id = selected;
      }
      const r = await axios.post(`${API}/pagarme/card/charge`, body);
      let data = r.data;
      let tries = 0;
      while (data.status === "pending" && tries < 6) {
        await new Promise(res => setTimeout(res, 2500));
        const s = await axios.get(`${API}/pagarme/card/${data.internal_code}`);
        data = { ...data, status: s.data.status };
        tries += 1;
      }
      if (data.status === "failed" || data.status === "canceled") throw new Error("Pagamento não autorizado pelo emissor do cartão.");
      setResult(data);
      if (data.card_save_error) toast.warning(`Pagamento ok, mas não foi possível salvar o cartão: ${data.card_save_error}`);
      else if (data.saved_card) toast.success("Cartão salvo para as próximas compras");
      onSuccess?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Falha no pagamento");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="card-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> {title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {result ? (
          <div className="text-center py-6 space-y-3" data-testid="card-payment-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <div className="text-lg font-bold">{result.status === "paid" ? "Pagamento aprovado!" : "Pagamento em processamento"}</div>
            <div className="text-sm text-muted-foreground">{brl(result.amount_brl)} em {result.installments}x de {brl(result.installment_value)}</div>
            <Button className="rounded-full" onClick={() => onOpenChange(false)} data-testid="card-payment-close">Concluir</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-muted/60 p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="text-xl font-bold" data-testid="card-payment-amount">{brl(amount)}</span>
            </div>

            {status && !status.configured && (
              <div className="text-xs text-amber-700 bg-amber-500/10 rounded-lg p-2" data-testid="card-payment-unconfigured">Gateway Pagar.me não configurado no servidor.</div>
            )}

            {cards.length > 0 && (
              <div className="space-y-2" data-testid="saved-cards-list">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Pagamento rápido</Label>
                {cards.map(c => <SavedCardRow key={c.card_id} card={c} selected={selected === c.card_id} onSelect={() => setSelected(c.card_id)} testid={`saved-card-${c.card_id}`} />)}
                <button type="button" data-testid="use-new-card" onClick={() => setSelected("new")}
                  className={`w-full flex items-center gap-2 p-3 rounded-xl border text-sm transition-colors ${isNew ? "border-primary bg-primary/5" : "border-dashed border-border hover:bg-muted"}`}>
                  <Plus className="w-4 h-4" /> Usar outro cartão
                </button>
              </div>
            )}

            {isNew && (
              <div className="space-y-3" data-testid="new-card-form">
                <div>
                  <Label htmlFor="cc-number" className="text-xs">Número do cartão</Label>
                  <Input id="cc-number" data-testid="cc-number" inputMode="numeric" autoComplete="cc-number" placeholder="0000 0000 0000 0000" value={form.number} onChange={(e) => setForm(f => ({ ...f, number: formatNumber(e.target.value) }))} />
                </div>
                <div>
                  <Label htmlFor="cc-holder" className="text-xs">Nome impresso no cartão</Label>
                  <Input id="cc-holder" data-testid="cc-holder" autoComplete="cc-name" placeholder="NOME COMO NO CARTÃO" value={form.holder} onChange={(e) => setForm(f => ({ ...f, holder: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cc-expiry" className="text-xs">Validade (MM/AA)</Label>
                    <Input id="cc-expiry" data-testid="cc-expiry" inputMode="numeric" placeholder="12/30" value={form.expiry} onChange={(e) => setForm(f => ({ ...f, expiry: formatExpiry(e.target.value) }))} />
                  </div>
                  <div>
                    <Label htmlFor="cc-cvv" className="text-xs">CVV</Label>
                    <Input id="cc-cvv" data-testid="cc-cvv" type="password" inputMode="numeric" autoComplete="cc-csc" placeholder="123" maxLength={4} value={form.cvv} onChange={(e) => setForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) }))} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid="cc-save" checked={saveCard} onCheckedChange={(v) => setSaveCard(!!v)} />
                  Salvar cartão para próximas compras (pagamento em 1 clique)
                </label>
              </div>
            )}

            <div>
              <Label htmlFor="cc-installments" className="text-xs">Parcelas (sem juros)</Label>
              <select id="cc-installments" data-testid="cc-installments" value={installments} onChange={(e) => setInstallments(Number(e.target.value))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {options.map(n => <option key={n} value={n}>{n}x de {brl(amount / n)}{n === 1 ? " (à vista)" : ""}</option>)}
              </select>
            </div>

            <Button data-testid="card-pay-btn" className="w-full rounded-full h-11 gap-2" disabled={busy || !formValid || !status?.configured} onClick={pay}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {busy ? "Processando..." : `Pagar ${brl(amount)}${installments > 1 ? ` em ${installments}x de ${brl(perInstallment)}` : ""}`}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Dados criptografados e enviados direto à Pagar.me. Nunca armazenamos o número do seu cartão.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
